import { faEllipsisV } from "@fortawesome/free-solid-svg-icons/faEllipsisV";
import { faTimes } from "@fortawesome/free-solid-svg-icons/faTimes";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as React from "react";
import { connect } from "react-redux";

import { ActionIcon, Alert, Menu } from "@mantine/core";
import { useMediaQuery } from "react-responsive";
import {
  addExtraOfficial,
  removeErrorFromGame,
  updateGame,
} from "../../../data/actions/gameActions";
import {
  getShiftById,
  removeReferee,
  rosterGame,
} from "../../../data/actions/shiftActions";
import { IState } from "../../../data/reducers/state";
import { IAssociation } from "../../../types/association/Association";
import { ICompetition } from "../../../types/competition/Competition";
import { IGame } from "../../../types/game/Game";
import {
  IGameConfiguration,
  IGameConfigurationField,
} from "../../../types/game/GameConfiguration";
import { Role } from "../../../types/persons/Role";
import { IShift } from "../../../types/shifts/Shift";
import { ITeam } from "../../../types/team/Team";
import { prepareColorForDarkMode } from "../../../util/string";
import { CompetitionLink, TeamLink } from "../../common/Links";
import GameManagerDialog from "../../game/GameManagerDialog";
import OfficialsRoster from "./OfficialsRoster";

const classes = {
  apprentice: "apprentice-cell",
  colorClash: "color-clash-cell",
};

interface IRosterGameCellProps {
  gameId: string; // _id of game

  // used to determine available referees + reload if game deletion
  shiftId: string;
  sessionId: string;
  hideTeamNames: boolean;

  style?: object | null;
  highlightProblemGames: boolean;
  gridView: string;
  showHideAppointmentFromSheetButton: boolean;

  // can be passed to officials selector, but

  actions: {
    rosterGame: Function;
    getShiftById: (
      shiftId: string,
      sessionId: string,
      gridView: string,
    ) => Promise<void>;
    removeReferee: (
      gameId: string,
      position: string,
      shiftSessionId: string,
    ) => Promise<void>;
    removeErrorFromGame: (gameId: string) => void;
    addExtraOfficial: (
      gameId: string,
      field: IGameConfigurationField,
      shiftId: string,
    ) => Promise<IGame>;
    updateGame: (game: Partial<IGame>) => Promise<any>;
  };
  game: IGame; // from state
  config: IGameConfiguration;
  teams: { [key: string]: ITeam }; // from state
  competitions: { [key: string]: ICompetition }; // from state
  associations: { [key: string]: IAssociation }; // from state
  shift: IShift; // from state
  tdproperties: any;
}

const RosterGameCell: React.FunctionComponent<IRosterGameCellProps> = (
  props,
) => {
  const [isDroppingDown, setIsDroppingDown] = React.useState(false);
  const [isManagingGame, setIsManagingGame] = React.useState(false);

  const removeErrors = () => {
    props.actions.removeErrorFromGame(props.gameId);
  };

  const onGameDelete = () => {
    setIsManagingGame(true);
    // trigger refresh from shiftID
    props.actions
      .getShiftById(props.shiftId, props.sessionId, props.gridView)
      .catch((er) =>
        alert(
          `Error refreshing shift: ${er.message}, please refresh the page to continue`,
        ),
      );
  };

  const addExtraOfficial = (role: Role) => {
    const field: IGameConfigurationField = {
      role,
      friendlyName: Role.toString(role),
      identifier: "",
    };

    props.actions.addExtraOfficial(props.gameId, field, props.shiftId).catch((er: Error) => {
      console.error('Failed to add extra official:', er);
      alert('Failed to add extra official: ' + er.message);
    });
  };

  const handleLabelToggle = (associationId: string, labelId: string) => {
    const currentLabels = Object.values(game.labels?.[associationId] || {});
    const hasLabel = currentLabels.includes(labelId);

    console.log('handleLabelToggle:', {
      associationId,
      labelId,
      currentLabels,
      hasLabel,
      gameLabels: game.labels
    });

    const newLabels = { ...game.labels };
    if (hasLabel) {
      // Remove label
      newLabels[associationId] = currentLabels.filter(id => id !== labelId);
      if (newLabels[associationId].length === 0) {
        delete newLabels[associationId];
      }
    } else {
      // Add label
      newLabels[associationId] = [...currentLabels, labelId];
    }

    console.log('handleLabelToggle - updating game:', {
      gameId: game._id,
      newLabels
    });

    props.actions.updateGame({
      _id: game._id,
      labels: newLabels
    }).then(() => {
      console.log('Labels updated successfully');
    }).catch((error) => {
      console.error('Failed to update labels:', error);
      alert('Failed to update game labels: ' + error.message);
    });
  };

  const {
    game,
    teams,
    competitions,
    associations,
    shift,
    shiftId,
    // @ts-ignore
    children,
    actions,
    config,
    highlightProblemGames,
    ...other
  } = props;

  if (!game) {
    return <p className="text-danger">Could not find game</p>;
  }

  const team1 = teams[game.team1];
  const team2 = teams[game.team2];
  const team1Name = team1 ? team1.name : "BYE";
  const team2Name = team2 ? team2.name : "BYE";
  const comp = game.competition ? competitions[game.competition] : null;
  const competition = comp ? comp.name : "";

  const refs = game.referees || {};
  const isColorClash =
    team1 && team2 && team1.color && team1.color == team2.color;
  const hasError = !game.loading && !!game.message;

  const isDarkMode = useMediaQuery({ query: "(prefers-color-scheme: dark)" });
  const backgroundColor = isDarkMode
    ? prepareColorForDarkMode(comp?.color)
    : comp?.color;

  return (
    <td
      style={{
        backgroundColor,
        width: "100%",
        height: "100%",
        ...(other.style || {}),
      }}
      {...(props.tdproperties || {})}
    >
      <Menu
        opened={isDroppingDown}
        onChange={setIsDroppingDown}
        position="bottom-end"
      >
        <Menu.Target>
          <ActionIcon
            variant="subtle"
            color="gray"
            size="sm"
            style={{
              marginRight: "5px",
              marginTop: "5px",
              float: "right",
            }}
          >
            <FontAwesomeIcon icon={faEllipsisV} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item onClick={() => setIsManagingGame(true)}>
            Edit
          </Menu.Item>
          <Menu.Divider />

          {/* Labels submenu */}
          {shift?.associationId && (
            <>
              <Menu.Sub>
                <Menu.Sub.Target>
                  <Menu.Sub.Item>Labels</Menu.Sub.Item>
                </Menu.Sub.Target>
                <Menu.Sub.Dropdown>
                  {(() => {
                    const associationId = shift.associationId;
                    const association = associations[associationId];
                    const associationLabels = association?.config?.labels || [];
                    const currentLabels = Object.values(game.labels?.[associationId] || {});

                    if (associationLabels.length === 0) return null;

                    return (
                      <div key={associationId}>
                        <Menu.Label>{association?.name || associationId}</Menu.Label>
                        {associationLabels.map(label => {
                          const hasLabel = currentLabels.includes(label._id);
                          return (
                            <Menu.Item
                              key={label._id}
                              onClick={() => handleLabelToggle(associationId, label._id)}
                              style={{
                                backgroundColor: hasLabel ? `${label.color}20` : undefined,
                                color: hasLabel ? label.color : undefined
                              }}
                            >
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                              }}>
                                <div
                                  style={{
                                    width: '12px',
                                    height: '12px',
                                    backgroundColor: label.color,
                                    borderRadius: '2px',
                                    border: '1px solid #ccc'
                                  }}
                                />
                                {label.name}
                                {hasLabel && <span style={{ marginLeft: 'auto' }}>✓</span>}
                              </div>
                            </Menu.Item>
                          );
                        })}
                      </div>
                    );
                  })()}
                </Menu.Sub.Dropdown>
              </Menu.Sub>
              <Menu.Divider />
            </>
          )}

          {[Role.referee, Role.scoretable, Role.statistician, Role.refereeCoach].map((r) => (
            <Menu.Item
              onClick={() => addExtraOfficial(r)}
              key={r}
            >
              Add {Role.toString(r)}
            </Menu.Item>
          ))}
        </Menu.Dropdown>
      </Menu>

      {/* <Button style={{ marginRight: "5px", marginTop: "5px", float: "right", padding: "3px", color: "#989898d6" }} onClick={toggleManager} color="text">
					<FontAwesomeIcon icon={faPen} />
				</Button> */}

      <div
        style={{
          marginRight: "20px",
          padding: "8px 10px 10px 14px",
          display: "flex",
          flexDirection: "column",
          gap: "4px",
        }}
      >
        <p>
          <CompetitionLink competitionId={game.competition}>
            <b>{competition}</b>
          </CompetitionLink>
        </p>
        {!props.hideTeamNames && (
          <p className={isColorClash ? classes.colorClash : ""}>
            <TeamLink teamId={game.team1}>{team1Name}</TeamLink> vs{" "}
            <TeamLink teamId={game.team2}>{team2Name}</TeamLink>
          </p>
        )}

        {/* Display applied labels */}
        {(() => {
          const labelsForAssociation = game.labels?.[shift?.associationId || ''] || {};
          const labelIds = Object.values(labelsForAssociation);

          return game.labels && shift?.associationId && labelIds.length > 0 && (
            <div
              style={{
                marginTop: "6px",
                display: "flex",
                flexWrap: "wrap",
                gap: "6px",
                justifyContent: "center",
                width: "100%",
              }}
            >
              {(() => {
                const associationId = shift.associationId;
                const association = associations[associationId];
                const associationLabels = association?.config?.labels || [];

                console.log('RosterGameCell Label Rendering:', {
                  associationId,
                  association: association?.name,
                  associationLabels: associationLabels.length,
                  labelIds,
                  associationsKeys: Object.keys(associations)
                });

                return labelIds.map(labelId => {
                  const label = associationLabels.find(l => l._id === labelId);
                  if (!label) return null;

                  return (
                    <span
                      key={`${associationId}-${labelId}`}
                      style={{
                        backgroundColor: `${label.color}20`,
                        color: label.color,
                        border: `1px solid ${label.color}40`,
                        borderRadius: 14,
                        padding: "3px 6px 3px 8px",
                        fontSize: "0.7rem",
                        fontWeight: 500,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px",
                        lineHeight: 1,
                        height: 20,
                        whiteSpace: "nowrap",
                        boxShadow: `0 0 0 1px ${label.color}30, 0 1px 2px 0 #00000040`,
                      }}
                    >
                      <span style={{ display: 'inline-block', maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis' }}>{label.name}</span>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleLabelToggle(associationId, String(labelId)); }}
                        aria-label={`Remove ${label.name} label`}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          padding: 0,
                          margin: 0,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          color: label.color,
                        }}
                      >
                        <FontAwesomeIcon icon={faTimes} style={{ fontSize: '0.6rem' }} />
                      </button>
                    </span>
                  );
                });
              })()}
            </div>
          );
        })()}
      </div>
      {/* {game.loading && <CenteredLoader />} */}
      {hasError && (
        <Alert color="red" onClose={removeErrors} withCloseButton>
          {game.message}
        </Alert>
      )}
      <div style={{ marginTop: "auto", marginBottom: "8px" }}>
        <OfficialsRoster
          gameId={props.gameId}
          shiftId={shiftId}
          sessionId={props.sessionId}
          officials={refs}
          highlightProblemGames={highlightProblemGames}
          startDate={game.date}
          venue={game.venue}
          showHideAppointmentFromSheetButton={props.showHideAppointmentFromSheetButton}
        />
      </div>
      <GameManagerDialog
        shiftId={shiftId}
        sessionId={props.sessionId}
        gameId={props.gameId}
        isOpen={isManagingGame}
        hide={() => setIsManagingGame(false)}
        onDelete={onGameDelete}
      />
    </td>
  );
};

function mapStateToProps(state: IState, ownProps: IRosterGameCellProps) {
  const game = state.games[ownProps.gameId];
  const shift = state.shifts.map[ownProps.shiftId];
  // const competition = state.competitions[game.competition]
  const configId = game.referees?.config || "twoPO";

  return {
    game,
    shift,
    // @ts-ignore
    teams: state.teams.map,
    // @ts-ignore until these get into IState
    competitions: state.competitions.map,
    associations: state.associations.map,
    config: state.gameConfigurations.map[configId],
  };
}

// NOTE: Typing 'dispatch' loosely here to avoid implicit any without pulling in Thunk types
function mapDispatchToProps(dispatch: any) {
  return {
    actions: {
      rosterGame: (
        gameId: string,
        person: string,
        position: string,
        hasApprentice: boolean,
        shiftSessionId: string,
        startDate: string,
        venue: string,
      ) =>
        dispatch(
          rosterGame(
            gameId,
            person,
            position,
            hasApprentice,
            shiftSessionId,
            startDate,
            venue,
          ),
        ),

      getShiftById: (shiftId: string, sessionId: string, gridView: string) =>
        dispatch(getShiftById(shiftId, sessionId, gridView)),
      removeReferee: (
        gameId: string,
        position: string,
        shiftSessionId: string,
      ) => dispatch(removeReferee(gameId, position, shiftSessionId)),

      removeErrorFromGame: (gameId: string) =>
        dispatch(removeErrorFromGame(gameId)),
      addExtraOfficial: (gameId: string, field: IGameConfigurationField, shiftId: string) =>
        dispatch(addExtraOfficial(gameId, field, shiftId)),
      updateGame: (game: Partial<IGame>) => dispatch(updateGame(game)),
    },
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(RosterGameCell);
